"use client";

import type { TextareaHTMLAttributes } from "react";

import { cn } from "@/lib/utils";

type GrowingTextareaProps = Omit<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    "value" | "onChange" | "rows"
> & {
    value: string;
    /** One paragraph: Enter does nothing and pasted line breaks become a space. */
    onValueChange: (value: string) => void;
    /** Keeps Enter and line breaks. */
    multiline?: boolean;
    /** Text style only (type, colour, wrapping): it also goes on the hidden
     *  sizing copy so both wrap alike. Layout classes go on a wrapper. */
    className?: string;
};

export function GrowingTextarea({
    value,
    placeholder,
    className,
    onValueChange,
    onKeyDown,
    multiline,
    ...rest
}: GrowingTextareaProps) {
    // The invisible copy sizes the grid cell, so the textarea grows with
    // wrapped text like the preview's <p> does.
    return (
        <div className="grid grid-cols-1">
            <span
                aria-hidden
                className={cn(className, "invisible col-start-1 row-start-1")}
            >
                {(value || placeholder || "") + " "}
            </span>
            <textarea
                rows={1}
                value={value}
                placeholder={placeholder}
                {...rest}
                onKeyDown={(e) => {
                    if (
                        !multiline &&
                        e.key === "Enter" &&
                        !e.nativeEvent.isComposing
                    )
                        e.preventDefault();
                    onKeyDown?.(e);
                }}
                onChange={(e) =>
                    onValueChange(
                        multiline
                            ? e.target.value
                            : e.target.value.replace(/\s*\n\s*/g, " ")
                    )
                }
                className={cn(
                    className,
                    "col-start-1 row-start-1 w-full resize-none overflow-hidden bg-transparent p-0 outline-none placeholder:text-[hsl(var(--lomda-muted-text))]"
                )}
            />
        </div>
    );
}
